import api from '@/api/client';
import { servicePath } from '@/config/env';
import {
  MOCK_ACADEMIC_YEARS,
  MOCK_GRADE_LEVELS,
  MOCK_SUBJECTS,
} from '@/features/class/manage/mockFormOptions';
import type { SelectOption } from '@/features/class/manage/types';

export interface ClassFormOptions {
  readonly academicYears: readonly SelectOption[];
  readonly subjects: readonly SelectOption[];
  readonly gradeLevels: readonly SelectOption[];
}

type MasterKind = 'subject' | 'grade';

const MASTER_FIELDS: Record<MasterKind, readonly string[]> = {
  subject: ['subjects', 'recentSubjects'],
  grade: ['grades', 'recentGrades'],
};

function pickString(source: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function collectMasterRows(payload: unknown, kind: MasterKind): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  const fields = MASTER_FIELDS[kind];
  const visit = (value: unknown) => {
    if (!value || typeof value !== 'object') return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          rows.push(item as Record<string, unknown>);
        }
      });
      return;
    }
    const record = value as Record<string, unknown>;
    for (const field of fields) {
      if (Array.isArray(record[field])) visit(record[field]);
    }
    visit(record.data);
    visit(record.result);
    visit(record.masters);
  };
  visit(payload);
  return rows;
}

function normalizeMasterOptions(payload: unknown, kind: MasterKind): SelectOption[] {
  const seen = new Set<string>();
  const labelKeys =
    kind === 'subject'
      ? ['name', 'subjectName', 'title', 'label', 'value']
      : ['name', 'gradeName', 'title', 'label', 'value'];
  const idKeys =
    kind === 'subject' ? ['id', 'subjectId', 'value', 'code'] : ['id', 'gradeId', 'value', 'code'];

  return collectMasterRows(payload, kind).flatMap((row) => {
    const id = pickString(row, idKeys);
    const label = pickString(row, labelKeys) ?? id;
    if (!id || !label || seen.has(id)) return [];
    seen.add(id);
    return [{ id, label }];
  });
}

async function fetchMasterOptions(masters: string, kind: MasterKind): Promise<SelectOption[]> {
  const { data } = await api.get<unknown>(servicePath('midl', 'master/getdata'), {
    params: { masters },
  });
  return normalizeMasterOptions(data, kind);
}

/** Loads dropdown options for the class form from `/midl/api/v3/master/getdata`. */
export async function fetchClassFormOptions(): Promise<ClassFormOptions> {
  const [subjectsResult, gradesResult] = await Promise.allSettled([
    fetchMasterOptions('subjects,recentSubjects', 'subject'),
    fetchMasterOptions('grades,recentGrades', 'grade'),
  ]);

  const subjects =
    subjectsResult.status === 'fulfilled' && subjectsResult.value.length > 0
      ? subjectsResult.value
      : MOCK_SUBJECTS;
  const gradeLevels =
    gradesResult.status === 'fulfilled' && gradesResult.value.length > 0
      ? gradesResult.value
      : MOCK_GRADE_LEVELS;

  return {
    academicYears: MOCK_ACADEMIC_YEARS,
    subjects,
    gradeLevels,
  };
}
